import { Vec3 } from './vec3.js';
import { BallMode, MAX_ALIGN_TICKS } from './constants.js';
import { rotationQuaternionYPR, rotateVectorF32 } from './f32.js';

export type BallId = number;

/**
 * A Destiny Ball: every simulated object in a ballpark is a sphere with
 * deterministic parametric movement. Field names track the C++ (mNewPos etc.)
 * closely so the port stays auditable against the reference source.
 */
export class Ball {
  id: BallId;
  radius = 0;
  mass = 0;
  maxVel = 0;
  agility = 1.0;
  speedFraction = 1.0;
  timeFactor = 1.0; // exp(-friction*dt/(mass*agility)), maintained by the park

  isFree = false;
  isMassive = false;
  isGlobal = false;
  isInteractive = false;
  isSpaceJunk = false;
  isMoribund = false;
  isCloaked = 0;
  inDeadBubble = false;

  mode: BallMode = BallMode.STOP;

  // Position/velocity double-buffers, swapped at the end of each evolve.
  newPos = new Vec3();
  newVel = new Vec3();
  oldPos = new Vec3();
  oldVel = new Vec3();
  newTime = 0; // timestamp of newPos (ms)
  oldTime = 0;

  // Mode state
  goto = new Vec3(); // GOTO destination / warp destination / missile launch ray
  followId: BallId = 0;
  followPtr: Ball | null = null;
  followRange = 0;
  /**
   * Multi-purpose like the C++: missile owner id, or warp factor for WARP mode
   * (the C++ shanghais mOwnerId for warp speed).
   */
  ownerId: BallId = 0;
  /**
   * Multi-purpose tick stamp: negative align counter while aligning for warp,
   * warp start tick once warping, missile launch tick, formation slot index.
   */
  effectStamp = 0;
  /** Repurposed during warp for total warp length (matches mLastCollision). */
  lastCollision = -1.0;
  /** Warp drop-in offset distance (C++ stuffs this into mFollowId's bits). */
  warpMinRange = 0;

  formationId = -1;
  formationSlots: (BallId | null)[] = [];
  followers = new Set<BallId>();

  // Per-tick accelerations
  lastG = new Vec3(); // behavior acceleration
  lastC = new Vec3(); // collision acceleration
  collisions: BallId[] = [];

  // Orientation (yaw/pitch/roll path; dynamical orientation is off, as in EVE)
  newYaw = 0;
  oldYaw = 0;
  newPitch = 0;
  oldPitch = 0;
  newRoll = 0;
  oldRoll = 0;
  newRollSpeed = 0;
  oldRollSpeed = 0;
  yawDelta = 0;

  constructor(id: BallId) {
    this.id = id;
  }

  isWarpish(): boolean {
    return this.mode === BallMode.WARP;
  }

  isAligningForWarp(): boolean {
    return this.isWarpish() && this.effectStamp < 0;
  }

  isWarping(): boolean {
    return this.isWarpish() && !this.isAligningForWarp();
  }

  isAlignedForWarp(): boolean {
    if (!this.isAligningForWarp()) return false;
    const dir = this.goto.clone().sub(this.newPos).normalize();
    const velDir = this.newVel.clone().normalize();
    if (
      Math.abs(1.0 - velDir.dot(dir)) < 0.01 &&
      this.newVel.lengthSq() > 0.5625 * this.maxVel * this.maxVel
    ) {
      return true;
    }
    // Force-start warp if the ship has been stuck aligning too long
    if (Math.abs(this.effectStamp) > MAX_ALIGN_TICKS) return true;
    return false;
  }

  /** Rotate a vector by this ball's current orientation (float32 path). */
  getRotatedVector(v: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
    const q = rotationQuaternionYPR(this.newYaw, this.newPitch, this.newRoll);
    return rotateVectorF32(v, q);
  }

  reserveFormationSlot(): number {
    for (let i = 0; i < this.formationSlots.length; i++) {
      if (this.formationSlots[i] === null) return i;
    }
    this.formationSlots.push(null);
    return this.formationSlots.length - 1;
  }

  freeFormationSlot(slot: number): void {
    if (slot >= 0 && slot < this.formationSlots.length) this.formationSlots[slot] = null;
  }
}
